import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { RevealWords, FadeUp } from "./Reveal";
import { WHITEPAPER } from "@/constants/testIds";

const IMG =
	"https://images.pexels.com/photos/17729218/pexels-photo-17729218.jpeg?auto=compress&cs=tinysrgb&w=1600";

const SECTIONS = [
	["01", "The real opportunity"],
	["02", "Built for another world"],
	["03", "The coordination layer"],
	["04", "Larger than Consumer AI"],
	["05", "Composable commerce"],
	["06", "Intelligent enterprise"],
	["07", "Enterprise memory"],
	["08", "Middle East advantage"],
	["09", "AI-native OS"],
	["10", "Still early"],
	["11", "Implications"],
	["12", "Conclusion"],
];

export default function Whitepaper() {
	const ref = useRef(null);
	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ["start end", "end start"],
	});
	const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

	return (
		<section
			id="whitepaper"
			ref={ref}
			data-testid={WHITEPAPER.section}
			className="relative py-12 md:py-16 border-t border-[var(--border-line)] bg-[var(--obsidian)] text-[var(--bone)] overflow-hidden"
		>
			<div className="max-container">
				<div className="mb-14">
						<div className="eyebrow mb-6">Further reading — Natant AI · Whitepaper 01</div>
						<h2 className="ctrl-h1 text-[14vw] md:text-[7.4vw]">
							<span className="block">
								<RevealWords trigger="inView" text="The Intelligent" />
							</span>
							<span className="block">
								<RevealWords
									trigger="inView"
									delay={0.15}
									text="Enterprise."
									className="neon-accent"
								/>
							</span>
						</h2>
					</div>

				<div className="grid grid-cols-12 gap-8 items-start">
					<div className="col-span-12 md:col-span-5">
						<div className="clip-frame aspect-[4/5] border border-[var(--border-line)]">
							<motion.img
								style={{ y }}
								src={IMG}
								alt="Architecture as foundation"
								className="scale-110"
							/>
							<div className="absolute inset-0 bg-gradient-to-t from-[var(--obsidian)]/85 via-transparent to-transparent" />
						</div>
						<FadeUp delay={0.1}>
							<a
								href="/whitepaper"
								data-testid={WHITEPAPER.link}
								className="pill-btn mt-8"
							>
								Read the paper · 14 min
							</a>
						</FadeUp>
					</div>

					<div className="col-span-12 md:col-span-7">
						<FadeUp>
							<p className="text-2xl md:text-[26px] leading-snug text-[var(--bone)]/90 mb-4 max-w-[46ch]">
								Why Enterprise AI will become the largest economic opportunity of the next decade — a working note on the emergence of AI-native business operating systems.
							</p>
						</FadeUp>
						<div className="mt-10 border-t border-[var(--border-line)]">
							{SECTIONS.map(([num, title], i) => (
								<motion.a
									key={num}
									href={`/whitepaper#section-${num}`}
									data-testid={WHITEPAPER.sectionItem}
									initial={{ opacity: 0, y: 12 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true, amount: 0.4 }}
									transition={{ duration: 0.55, delay: i * 0.025 }}
									className="group relative flex items-center justify-between border-b border-[var(--border-line)] py-4 md:py-5"
								>
									<div className="flex items-center gap-6">
										<span className="font-mono text-[11px] tracking-[0.22em] w-8 text-[var(--bone-dim)]">
											{num}
										</span>
										<span className="font-display text-lg md:text-2xl group-hover:text-[var(--terracotta)] group-hover:translate-x-2 transition-transform duration-500">
											{title}
										</span>
									</div>
									<span className="font-mono text-[var(--bone-dim)] group-hover:text-[var(--terracotta)] group-hover:translate-x-2 transition-transform">
										→
									</span>
								</motion.a>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
